import {QuerySampleItem, QuerySyntaxSection} from './types';

export const SYNTAX_SECTIONS: QuerySyntaxSection[] = [
    {
        id: 'operators',
        title: 'Basic operators',
        intro: 'These symbols and keywords can be combined in a single query.',
        rules: [
            {id: 'star', operator: '*', description: 'Wildcard matching any characters.', example: 'cancel*'},
            {id: 'question', operator: '?', description: 'Wildcard matching exactly one character.', example: 'ch?t'},
            {id: 'and', operator: 'AND', description: 'All terms must appear.', example: 'refund AND delayed'},
            {id: 'or', operator: 'OR', description: 'At least one term must appear.', example: 'refund OR reimbursement'},
            {id: 'not', operator: 'NOT', description: 'Excludes the following term.', example: 'refund NOT spam'},
            {id: 'minus', operator: '-', description: 'Shorthand exclusion prefix.', example: 'refund -spam'},
            {id: 'plus', operator: '+', description: 'Requires the following term (must appear).', example: '+refund delayed'},
            {id: 'quotes', operator: '"..."', description: 'Exact phrase match.', example: '"not satisfied"'},
            {id: 'tilde', operator: '~N', description: 'Fuzzy or proximity deviation (see below).', example: 'refund~2'},
            {id: 'tilde-default', operator: '~', description: 'Default fuzzy edit distance when no number is given.', example: 'refund~'},
            {id: 'boost', operator: '^N', description: 'Boosts relevance of a term.', example: 'refund^3 OR reimbursement'},
            {id: 'parens', operator: '( )', description: 'Group sub-queries for precedence.', example: '(refund OR reimbursement) AND delayed'},
            {id: 'field', operator: 'field:term', description: 'Search within a specific indexed field.', example: 'event_text:refund'},
        ],
    },
    {
        id: 'defaults',
        title: 'Default behavior',
        rules: [
            {
                id: 'case',
                operator: 'Case',
                description: 'Searches are case-insensitive.',
            },
            {
                id: 'implicit-or',
                operator: 'Spaces',
                description: 'Words typed without AND between them are matched as OR by default. Use AND when all terms are required.',
                example: 'refund delayed',
            },
            {
                id: 'smart-quotes',
                operator: 'Quotes',
                description: 'Curly/smart quotes are automatically normalized to straight quotes.',
            },
            {
                id: 'escape',
                operator: '\\',
                description: 'Escape literal special characters: ? * " ( ) [ ] { } ^ ~ : \\ / + - && || !',
                example: 'status\\?',
            },
        ],
    },
    {
        id: 'fields',
        title: 'Common search fields',
        intro: 'Use field:value syntax to target agent, customer, or channel-specific text.',
        rules: [
            {id: 'event-text', operator: 'event_text', description: 'Main event text content.', example: 'event_text:refund'},
            {id: 'event-agent', operator: 'event_agent_text', description: 'Agent-side text.', example: 'event_agent_text:"thank you"'},
            {id: 'event-customer', operator: 'event_customer_text', description: 'Customer-side text.', example: 'event_customer_text:(angry OR frustrated)'},
            {id: 'event-bot', operator: 'event_bot_text', description: 'Bot messages in chat.', example: 'event_bot_text:handoff'},
            {id: 'event-system', operator: 'event_system_text', description: 'System messages in chat.', example: 'event_system_text:timeout'},
        ],
    },
    {
        id: 'wildcards',
        title: 'Wildcards',
        rules: [
            {
                id: 'prefix',
                operator: 'word*',
                description: 'Matches strings starting with the prefix.',
                example: 'cancel*',
            },
            {
                id: 'suffix',
                operator: '*word',
                description: 'Matches strings ending with the suffix.',
                example: '*ment',
            },
            {
                id: 'infix',
                operator: 'wo*rd',
                description: 'Matches with unknown characters in the middle.',
                example: 'ref*nd',
            },
            {
                id: 'single',
                operator: '?',
                description: 'Matches a single unknown character.',
                example: 'ch?t',
            },
            {
                id: 'multi-single',
                operator: '??',
                description: 'Multiple ? placeholders match fixed-length unknown segments.',
                example: 'c??cel',
            },
            {
                id: 'leading-wildcard',
                operator: '*word',
                description: 'Leading wildcards may be slow or restricted depending on index settings. Prefer prefix wildcards when possible.',
                example: '*refund',
            },
        ],
    },
    {
        id: 'phrase-proximity',
        title: 'Phrases and proximity',
        rules: [
            {
                id: 'phrase',
                operator: '"phrase"',
                description: 'Requires words in the exact order.',
                example: '"not satisfied"',
            },
            {
                id: 'proximity',
                operator: '"phrase"~N',
                description: 'Allows up to N extra words inside the phrase.',
                example: '"agent was rude"~10',
            },
            {
                id: 'proximity-zero',
                operator: '"phrase"~0',
                description: 'Same as an exact phrase with zero slop.',
                example: '"not satisfied"~0',
            },
        ],
    },
    {
        id: 'fuzzy',
        title: 'Fuzzy search',
        rules: [
            {
                id: 'fuzzy-word',
                operator: 'word~N',
                description: 'Allows up to N character edits (typos).',
                example: 'cancelation~2',
            },
            {
                id: 'fuzzy-default',
                operator: 'word~',
                description: 'Uses the default fuzzy edit distance.',
                example: 'refund~',
            },
        ],
    },
    {
        id: 'field-range',
        title: 'Field ranges',
        rules: [
            {
                id: 'datetime-inclusive',
                operator: 'field:[A TO B]',
                description: 'Inclusive range — both bounds are included.',
                example: 'event_datetime:[2026-06-01T00:00:00 TO 2026-06-30T23:59:59]',
            },
            {
                id: 'datetime-exclusive',
                operator: 'field:{A TO B}',
                description: 'Exclusive range — bounds are excluded.',
                example: 'event_datetime:{2026-06-01 TO 2026-06-30}',
            },
            {
                id: 'datetime-open-start',
                operator: 'field:[* TO B]',
                description: 'Open start — everything up to the upper bound.',
                example: 'event_datetime:[* TO 2026-06-30T23:59:59]',
            },
            {
                id: 'datetime-open-end',
                operator: 'field:[A TO *]',
                description: 'Open end — everything from the lower bound onward.',
                example: 'event_datetime:[2026-06-01T00:00:00 TO *]',
            },
        ],
    },
    {
        id: 'mistakes',
        title: 'Common mistakes',
        intro: 'The query editor validates these while you type.',
        rules: [
            {id: 'unclosed-quote', operator: '"', description: 'Every opening quote must have a closing quote.'},
            {id: 'unbalanced-paren', operator: '( )', description: 'Parentheses must be balanced.'},
            {id: 'lowercase-bool', operator: 'AND / OR / NOT', description: 'Boolean operators must be uppercase.'},
            {id: 'tilde-space', operator: '~', description: 'Tilde must be attached to the preceding term or closing quote.'},
            {id: 'wildcard-in-phrase', operator: '"..."', description: 'Wildcards are not allowed inside quoted phrases.'},
            {id: 'bool-in-phrase', operator: '"..."', description: 'AND and OR are not treated as operators inside quotes.'},
        ],
    },
];

export const SYNTAX_ANTI_PATTERNS: QuerySampleItem[] = [
    {
        id: 'anti-unclosed-quote',
        title: 'Unclosed quote',
        query: '"not satisfied',
        description: 'Missing closing quotation mark.',
        variant: 'anti',
    },
    {
        id: 'anti-wildcard-in-phrase',
        title: 'Wildcard inside phrase',
        query: '"cancel*"',
        description: 'Wildcards cannot appear inside quoted phrases.',
        variant: 'anti',
    },
    {
        id: 'anti-or-in-phrase',
        title: 'Boolean operator inside phrase',
        query: '"refund or reimbursement"',
        description: 'OR is treated as literal text inside quotes, not as an operator.',
        variant: 'anti',
    },
    {
        id: 'anti-lowercase-and',
        title: 'Lowercase boolean operator',
        query: 'refund and delayed',
        description: 'Boolean operators must be uppercase: AND, OR, NOT.',
        variant: 'anti',
    },
    {
        id: 'anti-tilde-space',
        title: 'Space before proximity number',
        query: '"not satisfied" ~2',
        description: 'Tilde must be attached directly: "not satisfied"~2',
        variant: 'anti',
    },
    {
        id: 'anti-leading-wildcard',
        title: 'Leading wildcard (use with care)',
        query: '*refund',
        description: 'May be slow or unsupported. Prefer prefix wildcards like refund* when possible.',
        variant: 'anti',
    },
];

export const SYNTAX_TIPS: QuerySampleItem[] = [
    {
        id: 'good-to-know',
        title: 'Good to know',
        query: '',
        description: 'Use AND explicitly when you need every keyword. Use quotes for phrases. Group OR conditions with parentheses in complex queries. Curly quotes pasted from documents are normalized automatically.',
    },
];
